<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { formatUsdFromCents } from '../helpers/money';
import { evaluatePurchaseGate } from '../helpers/purchaseGuards';

const props = defineProps({
  open: { type: Boolean, default: false },
  title: { type: String, default: '' },
  summaryLines: { type: Array, default: () => [] },
  amountCents: { type: Number, default: 0 },
  balanceCents: { type: Number, default: 0 },
  isLoading: { type: Boolean, default: false },
  requiresPro: { type: Boolean, default: false },
  isPro: { type: Boolean, default: false },
  targetSeatLimit: { type: Number, default: null },
  seatsUsed: { type: Number, default: 0 },
  seatCap: { type: Number, default: null },
  walletSpendFrozen: { type: Boolean, default: false },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);

const gate = computed(() =>
  evaluatePurchaseGate({
    amountCents: props.amountCents,
    balanceCents: props.balanceCents,
    submitting: props.isLoading,
    requiresPro: props.requiresPro,
    isPro: props.isPro,
    targetSeatLimit: props.targetSeatLimit,
    seatsUsed: props.seatsUsed,
    seatCap: props.seatCap,
    walletSpendFrozen: props.walletSpendFrozen,
  })
);

const insufficient = computed(
  () => gate.value.reason === 'insufficient_balance'
);

const blockMessage = computed(() => {
  if (!gate.value.ok) {
    const key = `CUSTOM.BILLING.GATES.${String(gate.value.reason).toUpperCase()}`;
    const translated = t(key);
    return translated !== key ? translated : t('CUSTOM.BILLING.GATES.BLOCKED');
  }
  return '';
});

watch(
  () => props.open,
  value => {
    if (value) dialogRef.value?.open();
    else dialogRef.value?.close();
  }
);

const onConfirm = () => {
  if (!gate.value.ok || props.isLoading) return;
  emit('confirm');
};

const onClose = () => emit('close');
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    :title="title || t('CUSTOM.BILLING.CONFIRM.TITLE')"
    :description="t('CUSTOM.BILLING.CONFIRM.DESCRIPTION')"
    :confirm-button-label="t('CUSTOM.BILLING.CONFIRM.PAY')"
    :cancel-button-label="t('CUSTOM.BILLING.CONFIRM.CANCEL')"
    :disable-confirm-button="!gate.ok"
    :is-loading="isLoading"
    width="md"
    @confirm="onConfirm"
    @close="onClose"
  >
    <ul class="m-0 list-none space-y-2 text-sm text-n-slate-12">
      <li
        v-for="(line, idx) in summaryLines"
        :key="idx"
        class="flex justify-between gap-4"
      >
        <span class="text-n-slate-11">{{ line.label }}</span>
        <span class="font-medium text-end">{{ line.value }}</span>
      </li>
      <li class="flex justify-between gap-4 pt-2 border-t border-n-weak">
        <span class="text-n-slate-11">{{
          t('CUSTOM.BILLING.CONFIRM.AMOUNT')
        }}</span>
        <span class="font-semibold">{{ formatUsdFromCents(amountCents) }}</span>
      </li>
      <li class="flex justify-between gap-4">
        <span class="text-n-slate-11">{{
          t('CUSTOM.BILLING.CONFIRM.BALANCE')
        }}</span>
        <span>{{ formatUsdFromCents(balanceCents) }}</span>
      </li>
    </ul>
    <p
      v-if="blockMessage"
      class="mb-0 mt-3 text-sm"
      :class="insufficient ? 'text-n-amber-11' : 'text-n-ruby-9'"
      role="alert"
    >
      {{ blockMessage }}
    </p>
  </Dialog>
</template>
